// FarmerPulse — slim "My fruit at KF49" dashboard for FARM-kind tenants.
//
// When a farmer tenant logs in and switches to packhouse mode, they don't
// need (and shouldn't see) KF49's full Inventory Pulse. This panel shows
// just the slice that's about THEIR fruit — counts, recent deliveries,
// and outstanding invoices.
//
// The cross-tenant SELECT policies (mig 233/234) auto-scope every query
// here to the tenant's linked grower(s), so no explicit grower_id filter
// is needed in the supabase calls. Trying to query something not linked
// returns an empty list rather than an error.
//
// Counter-intuitive bit: pallets / runs / deliveries rows live in KF49's
// tenant (not the farmer's), so the queries appear to fetch "global" data.
// RLS does the filtering. Don't add a grower_id eq filter; let the policy
// do its job — that way, if a farmer has fruit at multiple packhouses
// later, all of it shows up automatically.
import { useNavigate } from 'react-router-dom'
import PalletIdText from '@/components/PalletIdText'
import { POLL } from '@/lib/pollTiers'
import { useQuery } from '@tanstack/react-query'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { supabase } from '@/lib/supabase'
import { ArrowRight, Truck, Package, FileText, Receipt } from 'lucide-react'
import clsx from 'clsx'
import QueryStateGate from '@/components/QueryStateGate'
import { Money } from '@/lib/money'
import { BrandLoader } from '@/components/BrandLoader'
import { fmtDayMonthZA } from '@/lib/dates'
import { unitNounFor } from '@/lib/unitNoun'

function sastMidnightISO() {
  const now = new Date(Date.now() + 2 * 60 * 60 * 1000)
  const ymd = now.toISOString().slice(0, 10)
  return new Date(`${ymd}T00:00:00+02:00`).toISOString()
}

interface FarmerStats {
  palletsInColdroom:      number
  palletsPackedToday:     number
  palletsDispatchedToday: number
  deliveriesThisWeek:     number
  recentDeliveries: {
    delivery_id:   string
    tenant_id:     string
    fruit_container_type: string | null
    received_at:   string | null
    bins_received: number
    status:        string
  }[]
  recentPallets: {
    pallet_id:   string
    ssc_id:      string | null
    packed_at:   string | null
    status:      string
    spec:        string | null
  }[]
  openInvoices: {
    invoice_id:     string
    invoice_number: string | null
    issued_at:      string | null
    total:          number
  }[]
}

async function fetchFarmerPulse(): Promise<FarmerStats> {
  const todayStart = sastMidnightISO()
  const weekAgo    = new Date(Date.now() - 7 * 86_400_000).toISOString()

  const [coldRes, packedTodayRes, dispatchedTodayRes, deliveriesWeekRes, recentDeliveriesRes, recentPalletsRes, openInvoicesRes] =
    await Promise.all([
      supabase.from('pallets').select('pallet_id', { count: 'exact', head: true })
        .eq('status', 'IN_COLDROOM').is('deleted_at', null),
      supabase.from('pallets').select('pallet_id', { count: 'exact', head: true })
        .gte('packed_at', todayStart).is('deleted_at', null),
      supabase.from('pallets').select('pallet_id', { count: 'exact', head: true })
        .eq('status', 'DISPATCHED').gte('dispatched_at', todayStart).is('deleted_at', null),
      supabase.from('deliveries').select('delivery_id', { count: 'exact', head: true })
        .gte('received_at', weekAgo).is('deleted_at', null),
      supabase.from('deliveries')
        .select('delivery_id, tenant_id, fruit_container_type, received_at, bins_received, status')
        .is('deleted_at', null)
        .order('received_at', { ascending: false })
        .limit(5),
      supabase.from('pallets')
        .select('pallet_id, ssc_id, packed_at, status, spec')
        .is('deleted_at', null)
        .order('packed_at', { ascending: false, nullsFirst: false })
        .limit(8),
      // "Open" = not paid and not voided. The invoices table has no `status`
      // column (open/paid/void is derived from paid_at/voided_at — see
      // Invoice.tsx / InvoicingTab / CashflowTab); the prior select referenced
      // non-existent columns (invoice_no / total_zar / status) and 400'd on the
      // farmer dashboard. Real columns: invoice_number, total, paid_at, voided_at.
      supabase.from('invoices')
        .select('invoice_id, invoice_number, issued_at, total')
        .is('deleted_at', null)
        .is('paid_at', null)
        .is('voided_at', null)
        .order('issued_at', { ascending: false, nullsFirst: false })
        .limit(5),
    ])

  // Throw on the first failure instead of degrading to zeroes. None of the
  // seven results had its `.error` checked, so the queryFn never threw and
  // react-query never entered isError: an RLS denial, a 400 or a timeout came
  // out as `count ?? 0` / `data ?? []`, which this component then presents as
  // fact — "0" pallets in the coldroom, "No deliveries yet", and the open-
  // invoices section gone entirely (it renders only when length > 0). This is
  // the grower-facing dashboard, so the lie is "your fruit isn't at the
  // packhouse and you have no unpaid invoices". The comment above records a
  // past incident where exactly this query set 400'd here — silently, which is
  // why it took an audit to find.
  for (const r of [coldRes, packedTodayRes, dispatchedTodayRes, deliveriesWeekRes,
                   recentDeliveriesRes, recentPalletsRes, openInvoicesRes]) {
    if (r.error) throw r.error
  }

  return {
    palletsInColdroom:      coldRes.count ?? 0,
    palletsPackedToday:     packedTodayRes.count ?? 0,
    palletsDispatchedToday: dispatchedTodayRes.count ?? 0,
    deliveriesThisWeek:     deliveriesWeekRes.count ?? 0,
    recentDeliveries:       (recentDeliveriesRes.data ?? []) as FarmerStats['recentDeliveries'],
    recentPallets:          (recentPalletsRes.data ?? []) as FarmerStats['recentPallets'],
    openInvoices:           (openInvoicesRes.data ?? []) as FarmerStats['openInvoices'],
  }
}

function StatTile({ label, value, sub, accent }: {
  label: string; value: number | string; sub?: string; accent?: string
}) {
  return (
    <div className="surface rounded-2xl p-3 text-center">
      <p className="text-xs text-fg-muted uppercase tracking-wider">{label}</p>
      <p className={clsx('text-2xl font-bold mt-1 tabular-nums', accent ?? 'text-fg')}>{value}</p>
      {sub && <p className="text-xs text-fg-muted mt-0.5">{sub}</p>}
    </div>
  )
}

// "12 bins" / "3 crates": the container the delivery was received in (a berry
// delivery is not "bins"). The code is an enum such as BLUEBERRY_CRATE or
// RED_ARM_LUG, and unitNounFor takes a plain word, so the last segment names
// it. No type recorded is the packhouse's usual bin.
export function deliveryCount(d: { bins_received: number; fruit_container_type: string | null }): string {
  const noun = unitNounFor([(d.fruit_container_type ?? 'BIN').split('_').pop()!])
  return `${d.bins_received} ${d.bins_received === 1 ? noun.singular : noun.plural}`
}

// Which packhouse a row came from, named only when the farm is linked to two
// or more (the caller checks) — a farm with one packhouse needs no label.
export function packhouseName(
  packhouses: ReadonlyArray<{ tenant_id: string; display_name: string }>, tenantId: string,
): string | null {
  return packhouses.find(p => p.tenant_id === tenantId)?.display_name ?? null
}

// Pre-existing render-state component; this change only adds the tenant to its
// query key (D4), which re-flags it. A deeper split is a separate refactor.
// fallow-ignore-next-line complexity
export default function FarmerPulse({ packhouses = [] }: { packhouses?: Array<{ tenant_id: string; display_name: string }> }) {
  const navigate = useNavigate()
  // Hold the query, not just its data — the two lists below need to tell an
  // empty packhouse apart from a failed read now that fetchFarmerPulse throws.
  // The key carries the business (D4): the persisted query cache otherwise
  // paints the PREVIOUS business's rows for a beat after a switch, and offline
  // keeps them.
  const tenantId = useActiveTenantId()
  const pulseQ = useQuery({
    queryKey: ['farmer-pulse', tenantId],
    enabled: !!tenantId,
    queryFn: fetchFarmerPulse,
    refetchInterval: POLL.standard,
  })
  const { data, isLoading } = pulseQ

  if (isLoading) return (
    <div className="flex justify-center py-12">
      <BrandLoader className="w-6 h-6" />
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Quick stats */}
      <p className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-1.5 px-1">My fruit at the packhouse — right now</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <StatTile label="In coldroom"     value={data?.palletsInColdroom ?? '—'}     sub="pallets" />
        <StatTile label="Packed today"    value={data?.palletsPackedToday ?? '—'} />
        <StatTile label="Dispatched today" value={data?.palletsDispatchedToday ?? '—'} />
        <StatTile label="Deliveries"      value={data?.deliveriesThisWeek ?? '—'}    sub="last 7 days" />
      </div>

      {/* Recent deliveries */}
      <section className="surface rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b hairline-strong">
          <p className="text-xs uppercase tracking-wider text-fg-muted font-semibold flex items-center gap-1.5">
            <Truck size={12} className="text-blue-400" /> Recent deliveries
          </p>
          <button onClick={() => navigate('/deliveries')} className="text-xs text-fg-muted hover:text-fg flex items-center gap-1">
            View all <ArrowRight size={12} />
          </button>
        </div>
        {(data?.recentDeliveries ?? []).length === 0 ? (
          <QueryStateGate query={pulseQ} noun="deliveries"
            empty={<p className="text-sm text-fg-muted px-3 py-6 text-center">No deliveries yet</p>} />
        ) : (
          <div className="divide-y divide-line">
            {data!.recentDeliveries.map(d => (
              <button key={d.delivery_id} onClick={() => navigate(`/deliveries/${d.delivery_id}`)}
                className="w-full text-left px-3 py-2 hover:bg-raised/50 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-fg">{deliveryCount(d)}</p>
                  <p className="text-xs text-fg-muted">
                    {packhouses.length >= 2 && packhouseName(packhouses, d.tenant_id) ? `${packhouseName(packhouses, d.tenant_id)} · ` : ''}
                    {fmtDayMonthZA(d.received_at)} · {d.status.replace(/_/g, ' ').toLowerCase()}
                  </p>
                </div>
                <ArrowRight size={14} className="text-fg-muted" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Recent pallets */}
      <section className="surface rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b hairline-strong">
          <p className="text-xs uppercase tracking-wider text-fg-muted font-semibold flex items-center gap-1.5">
            <Package size={12} className="text-indigo-400" /> Recent pallets
          </p>
          <button onClick={() => navigate('/pallets')} className="text-xs text-fg-muted hover:text-fg flex items-center gap-1">
            View all <ArrowRight size={12} />
          </button>
        </div>
        {(data?.recentPallets ?? []).length === 0 ? (
          <QueryStateGate query={pulseQ} noun="pallets"
            empty={<p className="text-sm text-fg-muted px-3 py-6 text-center">No pallets yet</p>} />
        ) : (
          <div className="divide-y divide-line">
            {data!.recentPallets.map(p => (
              <button key={p.pallet_id} onClick={() => navigate(`/pallets/${p.pallet_id}`)}
                className="w-full text-left px-3 py-2 hover:bg-raised/50 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-fg truncate">{p.ssc_id ?? <PalletIdText id={p.pallet_id} />}</p>
                  <p className="text-xs text-fg-muted">{p.spec ?? '—'} · {p.status.replace(/_/g, ' ').toLowerCase()}</p>
                </div>
                <p className="text-xs text-fg-muted shrink-0">{fmtDayMonthZA(p.packed_at)}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Open invoices */}
      {(data?.openInvoices ?? []).length > 0 && (
        <section className="surface rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b hairline-strong">
            <p className="text-xs uppercase tracking-wider text-fg-muted font-semibold flex items-center gap-1.5">
              <Receipt size={12} className="text-amber-400" /> Open invoices
            </p>
            <button onClick={() => navigate('/financial')} className="text-xs text-fg-muted hover:text-fg flex items-center gap-1">
              View all <ArrowRight size={12} />
            </button>
          </div>
          <div className="divide-y divide-line">
            {data!.openInvoices.map(i => (
              <div key={i.invoice_id} className="px-3 py-2 flex items-center gap-3">
                <FileText size={14} className="text-fg-muted" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-fg">{i.invoice_number ?? i.invoice_id.slice(0, 8)}</p>
                  <p className="text-xs text-fg-muted">{fmtDayMonthZA(i.issued_at)} · unpaid</p>
                </div>
                <p className="text-sm font-semibold text-fg tabular-nums"><Money value={Number(i.total ?? 0)} decimals={0} /></p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
